import { useCallback, Suspense, useRef } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Icon, Text } from "@zapier/design-system";
import { Typeahead, type Key } from "@zapier/design-system-beta";
import {
  tableAsyncAtomFamily,
  tablesAsyncAtom,
  takeSnapshotAtom,
} from "@zapier/canvas-state";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { ErrorBoundary } from "../../ErrorBoundary";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";
import { SelectedItem } from "../SelectedItem";
import styles from "./FloatingTablesTypeahead.module.css";

type Props = {
  selectedTableId: string | undefined;
};

function TablesTypeaheadLoader() {
  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search tables"
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <Typeahead.Item key="loading" id="loading">
          Loading tables...
        </Typeahead.Item>
      </Typeahead>
    </div>
  );
}

function SelectedTableSuspended({ selectedTableId }: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const selectedTable = useAtomValue(tableAsyncAtomFamily(selectedTableId));

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        tableId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  return (
    <SelectedItem
      label={selectedTable?.name || "Table not found"}
      icon={<Icon name="navTables" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function TablesTypeaheadSuspended() {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const tables = useAtomValue(tablesAsyncAtom);

  useFocusInput(inputRef);

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          tableId: key,
        },
      });

      if (key) {
        // TODO: emitEventOnTableConnect
      }
    },
    [takeSnapshot, updateNodeAndSave],
  );

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search tables"
        placeholder="Search tables"
        ref={inputRef}
        onSelectionChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        size="medium"
      >
        {tables.map((table) => {
          return (
            <Typeahead.Item
              key={table.id}
              id={table.id}
              textValue={table.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navTables" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{table.name}</Text>
              </div>
            </Typeahead.Item>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function TablesTypeahead({ selectedTableId }: Props) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<TablesTypeaheadLoader />}>
        {selectedTableId ? (
          <SelectedTableSuspended selectedTableId={selectedTableId} />
        ) : (
          <TablesTypeaheadSuspended />
        )}
      </Suspense>
    </ErrorBoundary>
  );
}

export function FloatingTablesTypeahead({ selectedTableId }: Props) {
  return (
    <NodeToolbarBottom>
      <MenuWrapper>
        <TablesTypeahead selectedTableId={selectedTableId} />
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
